import { Combobox } from "design-system/organisms/Combobox"
import { cn } from "design-system/utils"
import { Plus } from "lucide-react"
import { useMessengerIntegrationsQuery } from "modules/settings/hooks/messenger/useMessengerIntegrationsQuery"
import { useDebouncedValue } from "modules/shared/hooks/useDebouncedValue"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { MessengerIntegrationsResponseData } from "schema"
import { z } from "zod"

type Option =
  | (React.ComponentProps<typeof Combobox>["options"][number] & {
      data?: z.infer<typeof MessengerIntegrationsResponseData>[number]
    })
  | undefined

type MessengerPageFormSelectorProps = {
  onChange: (value: Option) => void
  placeholder: string
  emptyPlaceholder: string
  searchPlaceholder: string
  className?: string
  avoidDisplaySelected?: boolean
  hasCreateNew?: boolean
  value?: string | null
  isDisabled?: boolean
}

const MessengerPageFormSelector = ({
  onChange,
  placeholder,
  emptyPlaceholder,
  searchPlaceholder,
  className,
  avoidDisplaySelected,
  hasCreateNew,
  value,
  isDisabled,
}: MessengerPageFormSelectorProps) => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState("")
  const debouncedSearchQuery = useDebouncedValue({
    value: searchQuery,
  })
  const [selectedValue, setSelectedValue] = useState<Option>(undefined)

  const { data, isPending } = useMessengerIntegrationsQuery()

  const options = useMemo(() => {
    if (!data) return []

    return data
      .filter((page) => page.name.match(new RegExp(debouncedSearchQuery, "gi")))
      .map((page) => ({
        label: page.name,
        value: page.id,
        image: page.profilePicture ?? undefined,
        data: page,
      }))
  }, [data, debouncedSearchQuery])

  function onComboboxChange(option: Option) {
    if (!avoidDisplaySelected) {
      setSelectedValue(option)
    }
    onChange(option)
  }

  useEffect(() => {
    if (value && data) {
      const result = options.find((e) => e.value === value)

      if (result) {
        setSelectedValue(result)
      }
    }
  }, [value, data, options])

  return (
    <div className={cn("flex items-center space-x-4", className)}>
      <Combobox
        options={options}
        value={selectedValue}
        onChange={onComboboxChange}
        placeholder={placeholder}
        emptyPlaceholder={emptyPlaceholder}
        searchPlaceholder={searchPlaceholder}
        disabled={isPending || isDisabled}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        className="w-full"
        actions={
          hasCreateNew
            ? [
                {
                  icon: Plus,
                  text: t("campaigns.trigger.metadata.messengerDirectMessage.addAccount"),
                  to: "/settings/channels/messenger",
                  target: "_blank",
                },
              ]
            : undefined
        }
      />
    </div>
  )
}

export default MessengerPageFormSelector
